// Builds the self-contained print SVG: text converted to outlines, artwork
// embedded (SVG artwork stays vector), and a magenta CutContour layer.
import { renderSticker, escapeXml } from "./render";
import { loadFont } from "./fonts";

let inlineCounter = 0;

function n(value) {
    return Math.round(value * 100) / 100;
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Prefix ids and classes so several embedded SVGs cannot restyle each other.
function namespaceSvg(svgEl, prefix) {
    const idMap = {};
    svgEl.querySelectorAll("[id]").forEach(el => {
        const id = el.getAttribute("id");
        idMap[id] = `${prefix}${id}`;
        el.setAttribute("id", idMap[id]);
    });
    const fixRefs = value =>
        value.replace(/url\(\s*['"]?#([^'")\s]+)['"]?\s*\)/g, (m, id) =>
            idMap[id] ? `url(#${idMap[id]})` : m
        );
    svgEl.querySelectorAll("*").forEach(el => {
        Array.from(el.attributes).forEach(attr => {
            if (attr.name === "class") {
                el.setAttribute(
                    "class",
                    attr.value
                        .split(/\s+/)
                        .filter(Boolean)
                        .map(c => prefix + c)
                        .join(" ")
                );
            } else if (
                (attr.name === "href" || attr.name === "xlink:href") &&
                attr.value.startsWith("#") &&
                idMap[attr.value.slice(1)]
            ) {
                el.setAttribute(attr.name, `#${idMap[attr.value.slice(1)]}`);
            } else if (attr.value.includes("url(")) {
                el.setAttribute(attr.name, fixRefs(attr.value));
            }
        });
    });
    svgEl.querySelectorAll("style").forEach(style => {
        style.textContent = fixRefs(
            style.textContent.replace(
                /\.(-?[_a-zA-Z][\w-]*)/g,
                (m, cls) => `.${prefix}${cls}`
            )
        );
    });
}

function inlineSvgAsset(svgText) {
    const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
    const root = doc.documentElement;
    if (!root || root.nodeName.toLowerCase() !== "svg") {
        return null;
    }
    root.querySelectorAll("script, foreignObject").forEach(el => el.remove());
    inlineCounter += 1;
    namespaceSvg(root, `a${inlineCounter}-`);

    if (!root.getAttribute("viewBox")) {
        const w = parseFloat(root.getAttribute("width"));
        const h = parseFloat(root.getAttribute("height"));
        if (w > 0 && h > 0) {
            root.setAttribute("viewBox", `0 0 ${w} ${h}`);
        }
    }
    const viewBox = root.getAttribute("viewBox");
    const inner = Array.from(root.childNodes)
        .map(node => new XMLSerializer().serializeToString(node))
        .join("");

    return (box, preserve) =>
        `<svg x="${n(box.x)}" y="${n(box.y)}" width="${n(
            box.width
        )}" height="${n(box.height)}"${
            viewBox ? ` viewBox="${escapeXml(viewBox)}"` : ""
        } preserveAspectRatio="${preserve}" overflow="hidden">${inner}</svg>`;
}

const assetCache = new Map();

async function loadAsset(href, embedRaster) {
    try {
        const response = await fetch(href);
        if (!response.ok) {
            throw new Error(response.statusText);
        }
        const blob = await response.blob();
        if (blob.type.includes("svg") || /\.svg(\?|$)/i.test(href)) {
            const inline = inlineSvgAsset(await blob.text());
            if (inline) {
                return { inline };
            }
        }
        return embedRaster ? { href: await blobToDataUrl(blob) } : { href };
    } catch (e) {
        // Keep a reference rather than failing the whole order.
        return { href };
    }
}

/**
 * Resolves an image URL for the renderer. SVG artwork is always inlined so
 * the preview and the print scale it identically; raster images are only
 * embedded as data URLs for the print file.
 */
export function resolveAsset(href, { embedRaster = false } = {}) {
    if (!href) {
        return Promise.resolve(null);
    }
    if (href.startsWith("data:")) {
        return Promise.resolve({ href });
    }
    const key = `${embedRaster ? "embed" : "link"}:${href}`;
    if (!assetCache.has(key)) {
        assetCache.set(key, loadAsset(href, embedRaster));
    }
    return assetCache.get(key);
}

/**
 * sticker:  admin sticker record
 * design:   creator model (same shape as cart product_data)
 * hrefs:    { figure, background } image URLs (or data URLs)
 * figureAspect: width / height of the figure image as shown in the preview
 */
export async function buildPrintSvg(sticker, design, hrefs, figureAspect) {
    const [font, figure, background] = await Promise.all([
        loadFont(design.selectedFont),
        resolveAsset(hrefs.figure, { embedRaster: true }),
        resolveAsset(hrefs.background, { embedRaster: true })
    ]);

    return renderSticker(sticker, design, {
        mode: "print",
        font,
        figureAspect,
        uid: "print",
        title: `${sticker.sku} – ${(design.text || [])
            .filter(Boolean)
            .join(" / ")}`,
        assets: { figure, background }
    });
}
